import React, { useCallback, useState, lazy, memo } from 'react';
import { Check, Shield, Star, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
// Image URLs - moved outside component to prevent recreation
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/6qrrKJBNLbZo3x9zPgBFbW/PROYECTOS_Anuales_(5).png';
const BONUS_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/innVMTkTCmJ4eQoJahCdgb/31.png';
const BONUS_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/4HkkRvsjcK6FDMmMdQETVT/32.png';
const BONUS_3_IMAGE = 'https://cdn.magicpatterns.com/uploads/2qUaHoCzFsrqL3YNug2J72/33.png';
const PREVIEW_PAGES = ['https://cdn.magicpatterns.com/uploads/4Wpu89m7fg8HremdEtRQUj/Captura_de_pantalla_2026-02-01_a_la(s)_8.04.41_p._m..png', 'https://cdn.magicpatterns.com/uploads/f7HqMKdBEjAfeBHxePLsse/Captura_de_pantalla_2026-02-01_a_la(s)_8.04.52_p._m..png', 'https://cdn.magicpatterns.com/uploads/rymZHXikLgTK37vGAMCYL8/Captura_de_pantalla_2026-02-01_a_la(s)_8.05.03_p._m..png', 'https://cdn.magicpatterns.com/uploads/3U7L5WeUpARx2KB4562yMm/Captura_de_pantalla_2026-02-01_a_la(s)_8.05.13_p._m..png', 'https://cdn.magicpatterns.com/uploads/4S9rUT51d7LyY89UNfXwzz/Captura_de_pantalla_2026-02-01_a_la(s)_8.05.23_p._m..png', 'https://cdn.magicpatterns.com/uploads/kxZmxRDwMyi9ygdv5TcwCE/Captura_de_pantalla_2026-02-01_a_la(s)_8.05.38_p._m..png'];
// Static data moved outside component
const CHECKOUT_URL = 'https://checkout16.recursospro.shop/cart/45212460810284:1';
const TESTIMONIALS = [{
  name: 'María González',
  role: 'Prof. de Matemática',
  content: 'Antes pasaba horas buscando material. Ahora entro a clase con todo listo.'
}, {
  name: 'Carlos Ramírez',
  role: 'Secundaria',
  content: 'Me ahorró muchísimo tiempo. En minutos tengo la clase armada.'
}, {
  name: 'Ana Martínez',
  role: 'Matemática',
  content: 'Mis alumnos entienden más y yo explico con más seguridad.'
}];
const PROBLEM_ITEMS = ['Pasás horas planificando cada semana', 'Tenés material, pero está desordenado', 'Buscás ejercicios en mil lugares distintos', 'Guardás PDFs que después no encontrás', 'Terminás improvisando más de lo que te gustaría'];
const INCLUDES_ITEMS = ['6 cuadernillos completos (1° a 6° año)', 'Teoría clara explicada paso a paso', 'Ejercicios resueltos', 'Actividades prácticas', 'Evaluaciones y diagnósticos', 'Secuencias didácticas por unidad', 'Material visual', 'Planillas docentes'];
const BEFORE_ITEMS = ['Improvisación constante', 'Cansancio mental', 'Clases apuradas', 'Trabajo extra en casa', 'Estrés antes de cada clase'];
const AFTER_ITEMS = ['Clases claras y organizadas', 'Material listo siempre', 'Más seguridad al explicar', 'Menos trabajo fuera del aula', 'Más tranquilidad para enseñar'];
const BENEFITS_ITEMS = ['Acceso completo de por vida', 'Descarga inmediata', 'Material editable y reutilizable', 'Pago 100% seguro', 'Soporte por WhatsApp disponible'];
// Memoized star rating component
const StarRating = memo(({
  count = 5,
  size = 'w-5 h-5'
}: {
  count?: number;
  size?: string;
}) => <div className="flex gap-0.5 text-amber-400" data-id="element-11">
      {Array.from({
    length: count
  }, (_, i) => <Star key={i} className={`${size} fill-current`} data-id="element-12" />)}
    </div>);
// Memoized check item component
const CheckItem = memo(({
  text,
  variant = 'default'
}: {
  text: string;
  variant?: 'default' | 'green' | 'blue';
}) => {
  const colorClass = variant === 'green' ? 'text-green-500' : variant === 'blue' ? 'text-blue-500' : 'text-green-500';
  return <div className="flex items-center gap-3" data-id="element-13">
        <Check className={`w-5 h-5 ${colorClass} flex-shrink-0`} data-id="element-14" />
        <span className="text-gray-700" data-id="element-15">{text}</span>
      </div>;
});
export function LandingPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % PREVIEW_PAGES.length);
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + PREVIEW_PAGES.length) % PREVIEW_PAGES.length);
  }, []);
  const goToSlide = useCallback((idx: number) => {
    setCurrentSlide(idx);
  }, []);
  return <div className="min-h-screen bg-[#EEF2FF] font-sans text-gray-800" data-id="element-16">
      {/* Preload critical images */}
      <link rel="preload" as="image" href={MAIN_IMAGE} data-id="element-17" />

      {/* Hero Section */}
      <header className="pt-10 pb-16 px-4" data-id="element-18">
        <div className="max-w-3xl mx-auto text-center" data-id="element-19">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-5 py-2.5 mb-6 shadow-sm" data-id="element-20">
            <span className="text-lg" data-id="element-21">📐📘</span>
            <span className="text-blue-600 font-semibold text-sm tracking-wide" data-id="element-22">
              MEGAPACK MATEMÁTICA LISTA™
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold mb-6 text-gray-900 leading-tight" data-id="element-23">
            Todo el material de Matemática que necesitás para dar clases durante
            todo el año,{' '}
            <span className="text-blue-600" data-id="element-24">ya listo para usar.</span>
          </h1>

          <div className="my-8" data-id="element-25">
            <a href={CHECKOUT_URL} data-id="element-26">
              <img src={MAIN_IMAGE} alt="MegaPack Matemática LISTA" className="mx-auto max-w-full w-80 md:w-[420px]" width={420} height={420} fetchPriority="high" data-id="element-27" />
            </a>
          </div>

          <p className="text-xl md:text-2xl text-gray-500 mb-8" data-id="element-28">
            Sin improvisar. Sin perder horas planificando. Sin volver a empezar
            desde cero.
          </p>

          <div className="flex flex-col items-center gap-4 mb-6" data-id="element-29">
            <CTAButton text="👉 ACCEDER AHORA AL MEGAPACK" className="text-lg px-10 py-4 rounded-xl" scrollToPurchase data-id="element-30" />
            <p className="text-gray-400" data-id="element-31">Acceso inmediato · Producto digital</p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mt-8" data-id="element-32">
            <div className="flex items-center gap-2" data-id="element-33">
              <StarRating data-id="element-34" />
              <span className="text-gray-500" data-id="element-35">Valoración promedio</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-36">
              <Users className="w-5 h-5 text-gray-400" data-id="element-37" />
              <span className="text-gray-500" data-id="element-38">+2.800 docentes ya lo usan</span>
            </div>
          </div>
        </div>
      </header>

      {/* Problema Familiar */}
      <section className="py-20 px-4" data-id="element-39">
        <div className="max-w-3xl mx-auto text-center" data-id="element-40">
          <h2 className="text-2xl md:text-4xl font-bold text-blue-600 mb-10" data-id="element-41">
            Si sos docente de Matemática, esto te va a sonar familiar...
          </h2>

          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm" data-id="element-42">
            <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-43">
              Llegás a tu casa cansado/a. Sabés que tenés que preparar la
              próxima clase. Abrís la compu… y empezás otra vez desde cero.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-44">
              Buscar ejercicios. Pensar problemas que se entiendan. Armar
              actividades. Preparar evaluaciones. Ordenar material suelto.
            </p>
            <div className="space-y-3 mb-8 text-left max-w-md mx-auto" data-id="element-45">
              <p className="text-gray-700 flex items-center gap-3" data-id="element-46">
                <span className="text-red-400" data-id="element-47">✕</span>No porque no sepas
                Matemática.
              </p>
              <p className="text-gray-700 flex items-center gap-3" data-id="element-48">
                <span className="text-red-400" data-id="element-49">✕</span>No porque no seas buen
                docente.
              </p>
            </div>
            <p className="text-xl font-semibold text-gray-800" data-id="element-50">
              El problema es que cada clase parece empezar desde cero.
            </p>
          </div>
        </div>
      </section>

      {/* Problema Real */}
      <section className="py-16 px-4 bg-white" data-id="element-51">
        <div className="max-w-3xl mx-auto" data-id="element-52">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-600 mb-4" data-id="element-53">
            El problema real
          </h2>
          <p className="text-center text-gray-400 mb-10" data-id="element-54">(El que nadie dice)</p>
          <p className="text-center text-gray-600 mb-10 text-lg" data-id="element-55">
            Si sos docente de Matemática, seguramente vivís esto:
          </p>

          <div className="space-y-4 mb-10 max-w-xl mx-auto" data-id="element-56">
            {PROBLEM_ITEMS.map((item, idx) => <div key={idx} className="flex items-center gap-4 bg-gray-50 p-4 rounded-xl" data-id="element-57">
                <span className="text-gray-300" data-id="element-58">•</span>
                <span className="text-gray-700" data-id="element-59">{item}</span>
              </div>)}
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-xl max-w-xl mx-auto" data-id="element-60">
            <p className="text-gray-700 italic text-center text-lg" data-id="element-61">
              Y esa sensación constante de:
              <br data-id="element-62" />
              <span className="font-semibold not-italic block mt-2 text-gray-800" data-id="element-63">
                "Podría dar mejores clases… si tuviera todo el material listo."
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Solución */}
      <section className="py-20 px-4 bg-[#1e3a5f] text-white" data-id="element-64">
        <div className="max-w-3xl mx-auto text-center" data-id="element-65">
          <p className="text-blue-300 font-semibold text-sm uppercase tracking-wider mb-3" data-id="element-66">
            La solución
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-67">
            📘 MEGAPACK MATEMÁTICA LISTA™
          </h2>
          <p className="text-blue-100 mb-10 max-w-2xl mx-auto text-lg leading-relaxed" data-id="element-68">
            Esto no es un conjunto de PDFs sueltos.
            <br data-id="element-69" />
            <br data-id="element-70" />
            Es un sistema completo de recursos de Matemática, organizado por año
            y por tema, diseñado para que puedas dar clases durante todo el año
            sin improvisar.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-2xl mx-auto mb-10" data-id="element-71">
            <div className="bg-white/10 p-6 rounded-xl" data-id="element-72">
              <div className="text-4xl mb-3" data-id="element-73">📂</div>
              <p className="font-medium" data-id="element-74">Abrís el material</p>
            </div>
            <div className="bg-white/10 p-6 rounded-xl" data-id="element-75">
              <div className="text-4xl mb-3" data-id="element-76">👆</div>
              <p className="font-medium" data-id="element-77">Elegís el tema</p>
            </div>
            <div className="bg-white/10 p-6 rounded-xl" data-id="element-78">
              <div className="text-4xl mb-3" data-id="element-79">✏️</div>
              <p className="font-medium" data-id="element-80">Lo aplicás en clase</p>
            </div>
          </div>

          <p className="text-blue-200 mb-10 text-lg" data-id="element-81">
            Sin vueltas. Sin improvisar. Sin perder tiempo.
          </p>
          <CTAButton text="👉 QUIERO ACCESO INMEDIATO" className="text-lg px-10 py-4 rounded-xl bg-blue-500 hover:bg-blue-400" scrollToPurchase data-id="element-82" />
        </div>
      </section>

      {/* Qué incluye */}
      <section className="py-20 px-4 bg-white" data-id="element-83">
        <div className="max-w-3xl mx-auto" data-id="element-84">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-600 mb-12" data-id="element-85">
            ¿Qué incluye exactamente el MegaPack?
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-10" data-id="element-86">
            {INCLUDES_ITEMS.map((item, idx) => <div key={idx} className="flex items-center gap-3 bg-gray-50 p-4 rounded-xl" data-id="element-87">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-88" />
                <span className="text-gray-700" data-id="element-89">{item}</span>
              </div>)}
          </div>
          <p className="text-center text-gray-500 text-lg" data-id="element-90">
            Todo organizado para encontrar rápido lo que necesitás y cubrir todo
            el programa.
          </p>
        </div>
      </section>

      {/* Preview Carousel */}
      <section className="py-20 px-4 bg-[#EEF2FF]" data-id="element-91">
        <div className="max-w-4xl mx-auto" data-id="element-92">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-600 mb-4" data-id="element-93">
            👀 Mirá el material por dentro
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-94">
            Páginas reales del MegaPack para que veas la calidad del contenido
          </p>

          <div className="relative" data-id="element-95">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 overflow-hidden" data-id="element-96">
              <img src={PREVIEW_PAGES[currentSlide]} alt={`Página de muestra ${currentSlide + 1}`} className="w-full h-auto rounded-lg shadow-sm" loading="lazy" data-id="element-97" />
            </div>

            <button onClick={prevSlide} className="absolute left-2 md:-left-5 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-50 text-gray-700 p-3 rounded-full shadow-lg" aria-label="Anterior" data-id="element-98">
              <ChevronLeft className="w-6 h-6" data-id="element-99" />
            </button>
            <button onClick={nextSlide} className="absolute right-2 md:-right-5 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-50 text-gray-700 p-3 rounded-full shadow-lg" aria-label="Siguiente" data-id="element-100">
              <ChevronRight className="w-6 h-6" data-id="element-101" />
            </button>
          </div>

          <div className="flex justify-center gap-2 mt-6" data-id="element-102">
            {PREVIEW_PAGES.map((_, idx) => <button key={idx} onClick={() => goToSlide(idx)} className={`w-3 h-3 rounded-full ${idx === currentSlide ? 'bg-blue-600 w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Ir a página ${idx + 1}`} data-id="element-103" />)}
          </div>
          <p className="text-center text-gray-400 text-sm mt-4" data-id="element-104">
            Página {currentSlide + 1} de {PREVIEW_PAGES.length}
          </p>
        </div>
      </section>

      {/* Antes vs Después */}
      <section className="py-20 px-4 bg-[#EEF2FF]" data-id="element-105">
        <div className="max-w-4xl mx-auto" data-id="element-106">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-600 mb-12" data-id="element-107">
            Antes vs Después
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-108">
            <div className="bg-white p-8 rounded-2xl shadow-sm" data-id="element-109">
              <h3 className="text-lg font-semibold mb-6 text-gray-500 flex items-center gap-2" data-id="element-110">
                <span className="text-red-400" data-id="element-111">✕</span>Antes del MegaPack
              </h3>
              <div className="space-y-4" data-id="element-112">
                {BEFORE_ITEMS.map((item, idx) => <div key={idx} className="flex items-center gap-3 text-gray-500" data-id="element-113">
                    <span className="text-gray-300" data-id="element-114">•</span>
                    <span data-id="element-115">{item}</span>
                  </div>)}
              </div>
            </div>
            <div className="bg-blue-50 p-8 rounded-2xl shadow-sm border border-blue-100" data-id="element-116">
              <h3 className="text-lg font-semibold mb-6 text-blue-600 flex items-center gap-2" data-id="element-117">
                <Check className="w-5 h-5" data-id="element-118" />
                Después del MegaPack
              </h3>
              <div className="space-y-4" data-id="element-119">
                {AFTER_ITEMS.map((item, idx) => <div key={idx} className="flex items-center gap-3 text-gray-700" data-id="element-120">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" data-id="element-121" />
                    <span data-id="element-122">{item}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bonos */}
      <section className="py-20 px-4 bg-white" data-id="element-123">
        <div className="max-w-4xl mx-auto" data-id="element-124">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-600 mb-3" data-id="element-125">
            🎁 Bonos incluidos
          </h2>
          <p className="text-center text-gray-400 mb-12" data-id="element-126">Sin costo extra</p>

          <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-127">
            <a href={CHECKOUT_URL} className="group" data-id="element-128">
              <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg h-full" data-id="element-129">
                <img src={BONUS_1_IMAGE} alt="Kit Premium de Evaluaciones" className="w-full rounded-xl mb-4" loading="lazy" width={300} height={200} data-id="element-130" />
                <h3 className="font-semibold text-gray-800 mb-2" data-id="element-131">
                  🎁 Bono 1 – Kit Premium de Evaluaciones
                </h3>
                <p className="text-sm text-gray-500" data-id="element-132">
                  Más de 50 evaluaciones listas con criterios de corrección.
                </p>
              </div>
            </a>
            <a href={CHECKOUT_URL} className="group" data-id="element-133">
              <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg h-full" data-id="element-134">
                <img src={BONUS_2_IMAGE} alt="Banco de Juegos" className="w-full rounded-xl mb-4" loading="lazy" width={300} height={200} data-id="element-135" />
                <h3 className="font-semibold text-gray-800 mb-2" data-id="element-136">
                  🎁 Bono 2 – Banco de Juegos y Actividades
                </h3>
                <p className="text-sm text-gray-500" data-id="element-137">
                  Más de 30 recursos lúdicos para motivar tus clases.
                </p>
              </div>
            </a>
            <a href={CHECKOUT_URL} className="group" data-id="element-138">
              <div className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg h-full" data-id="element-139">
                <img src={BONUS_3_IMAGE} alt="Plantillas Profesionales" className="w-full rounded-xl mb-4" loading="lazy" width={300} height={200} data-id="element-140" />
                <h3 className="font-semibold text-gray-800 mb-2" data-id="element-141">
                  🎁 Bono 3 – Plantillas de Planificación
                </h3>
                <p className="text-sm text-gray-500" data-id="element-142">
                  Modelos semanales y mensuales para organizar todo el año.
                </p>
              </div>
            </a>
          </div>
          <p className="text-center text-gray-500 font-medium" data-id="element-143">
            Todo incluido en el acceso.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="purchase-section" className="py-20 px-4 bg-[#EEF2FF] scroll-mt-20" data-id="element-144">
        <div className="max-w-lg mx-auto" data-id="element-145">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-id="element-146">
            <div className="bg-[#1e3a5f] py-6 px-8 text-center" data-id="element-147">
              <p className="text-blue-300 text-sm font-medium uppercase tracking-wider mb-2" data-id="element-148">
                OFERTA ESPECIAL
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white" data-id="element-149">
                📐 MEGAPACK MATEMÁTICA LISTA™
              </h2>
            </div>

            <div className="p-8 text-center" data-id="element-150">
              <p className="text-gray-400 mb-1" data-id="element-151">Precio regular</p>
              <p className="text-gray-400 line-through text-xl mb-4" data-id="element-152">
                $49.990 ARS
              </p>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2" data-id="element-153">
                PRECIO HOY
              </p>
              <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-154">
                <span className="text-5xl md:text-6xl font-bold text-blue-600" data-id="element-155">
                  $19.990
                </span>
                <span className="text-2xl font-semibold text-gray-700" data-id="element-156">
                  ARS
                </span>
              </div>
              <p className="text-gray-400 text-sm mb-4" data-id="element-157">(Pesos argentinos)</p>
              <p className="text-green-500 font-semibold mb-8" data-id="element-158">
                💰 Ahorrás $30.000 ARS
              </p>

              <CTAButton text="👉 ACCEDER AHORA AL MEGAPACK" fullWidth className="text-lg py-4 rounded-xl mb-4" data-id="element-159" />
              <p className="text-gray-400 text-sm mb-8" data-id="element-160">
                Acceso inmediato · Descarga digital
              </p>

              <div className="border-t border-gray-100 pt-8" data-id="element-161">
                <p className="font-semibold text-gray-700 mb-6 italic" data-id="element-162">
                  Lo que recibís hoy:
                </p>
                <div className="space-y-4 text-left" data-id="element-163">
                  {BENEFITS_ITEMS.map((item, idx) => <CheckItem key={idx} text={item} data-id="element-164" />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Garantía */}
      <section className="py-16 px-4 bg-white" data-id="element-165">
        <div className="max-w-xl mx-auto text-center" data-id="element-166">
          <h2 className="text-2xl md:text-3xl font-bold text-blue-600 mb-8" data-id="element-167">
            Garantía sin riesgo
          </h2>
          <div className="bg-gray-50 rounded-2xl p-8" data-id="element-168">
            <Shield className="w-14 h-14 text-blue-500 mx-auto mb-4" data-id="element-169" />
            <h3 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-170">
              🛡️ Garantía de 7 días
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-171">
              Probalo tranquilo/a. Si no es para vos, te devolvemos el 100%.
              <br data-id="element-172" />
              Sin letra chica. Todo el riesgo es nuestro.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-20 px-4 bg-[#EEF2FF]" data-id="element-173">
        <div className="max-w-4xl mx-auto" data-id="element-174">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-600 mb-12" data-id="element-175">
            Lo que dicen otros docentes
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-176">
            {TESTIMONIALS.map((t, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm" data-id="element-177">
                <StarRating count={5} size="w-4 h-4" data-id="element-178" />
                <p className="text-gray-600 italic my-4" data-id="element-179">"{t.content}"</p>
                <p className="font-semibold text-gray-800" data-id="element-180">{t.name}</p>
                <p className="text-sm text-gray-500" data-id="element-181">{t.role}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 bg-[#1e3a5f] text-white text-center" data-id="element-182">
        <div className="max-w-2xl mx-auto" data-id="element-183">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" data-id="element-184">
            📘 Accedé ahora al MegaPack Matemática Lista
          </h2>
          <p className="text-blue-200 mb-8" data-id="element-185">Acceso digital inmediato</p>
          <CTAButton text="👉 OBTENER MI MEGAPACK AHORA" className="text-lg px-10 py-4 rounded-xl bg-blue-500 hover:bg-blue-400" data-id="element-186" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f1f33] text-gray-400 py-10 px-4 text-center text-sm" data-id="element-187">
        <div className="max-w-4xl mx-auto space-y-3" data-id="element-188">
          <p data-id="element-189">Producto digital · Acceso inmediato · Pago seguro</p>
          <p className="text-gray-500" data-id="element-190">
            Este sitio no está afiliado ni patrocinado por Facebook.
          </p>
        </div>
      </footer>
    </div>;
}